
import { IconCaretDownFilled, IconCaretRightFilled, IconGripVertical, IconPlus, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import {
    useEffect, useMemo, useState,
} from 'react';
import classNames from 'classnames';
import {
    DndContext, closestCenter, DragEndEvent, KeyboardSensor, Modifier, PointerSensor, useDndMonitor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IProFormaUses, DevelopmentUnitOfMeasureTypes, IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { NSButton, NSCheckbox } from 'bricks';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES } from 'constants/unitTypes';
import { DEFAULT_BUDGET_LINE_NAME } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useUsesTableContext } from '../../context/UsesTableProvider';
import UsesTableBudgetLine from './UsesTableBudgetLine/UsesTableBudgetLine';
import PeriodCellValue from '../../PeriodCellValue/PeriodCellValue';
import './UsesTableSubcategory.scss';

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

export interface IUsesTableSubcategory {
    subcategory: IProFormaUses;
    unitType: DevelopmentUnitOfMeasureTypes;
    projectionPeriods: IFormattedCashFlowPeriod[];
    isPreview?: boolean;
    isSortable?: boolean;
}

const UsesTableSubcategory = ({
    subcategory, unitType, projectionPeriods, isPreview, isSortable = true,
}: IUsesTableSubcategory) => {
    const {
        selectedCategories, expandedCategories, updateSelectedCategories,
        updateExpandedCategories, isProFormaReadOnly,
    } = useUsesTableContext();

    const {
        handleCreateBudgetClassification,
        isBudgetClassificationLoading,
        handleUpdateBudgetClassifications,
        handleDeleteBudgetClassification,
        isBudgetClassificationDeletionLoading,
        showCents,
        periodCadence,
    } = useProFormaSocketContext();

    const [isHovered, setIsHovered] = useState(false);
    const [openDeleteSubcategoryModal, setOpenDeleteSubcategoryModal] = useState(false);

    const toggleDeleteSubcategoryModal = () => setOpenDeleteSubcategoryModal(!openDeleteSubcategoryModal);

    const propChildIds = useMemo(
        () => (subcategory.children || []).map(child => child.id),
        [subcategory.children],
    );

    const [orderedChildIds, setOrderedChildIds] = useState<number[]>(propChildIds);

    // Adopt the server-driven order when it changes; same-order updates stay a no-op.
    useEffect(() => {
        setOrderedChildIds(prev => {
            if (
                prev.length === propChildIds.length
                && prev.every((id, index) => id === propChildIds[index])
            ) {
                return prev;
            }
            return propChildIds;
        });
    }, [propChildIds]);

    const orderedChildren = useMemo(() => {
        const byId = new Map((subcategory.children || []).map(child => [child.id, child]));
        return orderedChildIds
            .map(id => byId.get(id))
            .filter((child): child is NonNullable<typeof child> => Boolean(child));
    }, [orderedChildIds, subcategory.children]);

    const editableBudgetClassificationIds = orderedChildren.map(
        budgetClassification => (budgetClassification.isDeletable ? budgetClassification.id : null),
    ).filter(id => id !== null);

    const canDrag = isSortable && !isPreview && !isProFormaReadOnly && !subcategory.isCalculated;

    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: subcategory.id, disabled: !canDrag });

    // Override dnd-kit's snap-to-slot transform with the raw pointer delta on the active
    // row — subcategory items are measured by their header only, so the snap delta is
    // wrong when the expanded group height differs from the swapped sibling's height.
    const [rawPointerDelta, setRawPointerDelta] = useState<{ x: number; y: number } | null>(null);

    useDndMonitor({
        onDragMove(event) {
            if (event.active.id === subcategory.id) setRawPointerDelta(event.delta);
        },
        onDragEnd(event) {
            if (event.active.id === subcategory.id) setRawPointerDelta(null);
        },
        onDragCancel(event) {
            if (event.active.id === subcategory.id) setRawPointerDelta(null);
        },
    });

    const subcategoryTransformStyle = isDragging && rawPointerDelta
        ? `translate3d(0px, ${rawPointerDelta.y}px, 0)`
        : CSS.Transform.toString(transform);

    const activeTransition = isDragging ? undefined : transition;

    const rowStyle = canDrag
        ? { transform: subcategoryTransformStyle, transition: activeTransition, opacity: isDragging ? 0.6 : 1 }
        : undefined;

    // Share the subcategory's transform with its children so the whole group moves together.
    const childDragStyle = useMemo(
        () => (
            subcategoryTransformStyle
                ? { transform: subcategoryTransformStyle, transition: activeTransition, opacity: isDragging ? 0.6 : 1 }
                : undefined
        ),
        [subcategoryTransformStyle, activeTransition, isDragging],
    );

    const childIds = orderedChildIds;

    const linesSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleBudgetLineDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        let computedNewIndex: number | null = null;
        setOrderedChildIds(prev => {
            const oldIndex = prev.indexOf(active.id as number);
            const newIndex = prev.indexOf(over.id as number);
            if (oldIndex === -1 || newIndex === -1) return prev;
            computedNewIndex = newIndex;
            return arrayMove(prev, oldIndex, newIndex);
        });
        if (computedNewIndex !== null) {
            handleUpdateBudgetClassifications([{ id: active.id as number, position: computedNewIndex }]);
        }
    };

    const expandCategory = () => {
        if (!expandedCategories.subcategories.includes(subcategory.id)) {
            updateExpandedCategories(null, subcategory.id);
        }
    };

    const createBudgetLine = () => {
        const postBody = {
            budgetLineNickname: DEFAULT_BUDGET_LINE_NAME,
            parentId: subcategory.id,
        };
        handleCreateBudgetClassification(postBody, expandCategory);
    };

    const deleteSubcategory = () => {
        handleDeleteBudgetClassification([subcategory.id], false, toggleDeleteSubcategoryModal);
    };

    const selectedUnitTotal = subcategory[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]];
    const hasNoSelectedCategories = Object.values(selectedCategories).every(categoryLevel => !categoryLevel.length);

    let subcategoryPeriods = subcategory.periods || [];
    if (periodCadence === Periods.ANNUALLY) subcategoryPeriods = subcategory.yearlyPeriods || [];

    return (
        <>
            <tr
                ref={setNodeRef}
                style={rowStyle}
                className="NSTable__tbody__tr NSTable__tbody__tr--level-2"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <td
                    className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border NSTable__tbody__tr__td__input text-dark"
                >
                    <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                            <RenderIf isTrue={!isProFormaReadOnly && !isPreview}>
                                <NSCheckbox
                                    id={`selectSubcategory-${subcategory.id}`}
                                    checked={selectedCategories.subcategories.includes(subcategory.id)}
                                    callback={() => updateSelectedCategories(null, [subcategory.id], editableBudgetClassificationIds as number[])}
                                    containerClassName="custom-control-inline align-middle"
                                />
                            </RenderIf>
                            <RenderIf isTrue={canDrag}>
                                <span
                                    {...attributes}
                                    {...listeners}
                                    aria-label="Drag to reorder subcategory"
                                    className={classNames('UsesTableSubcategory__dragHandle mr-2', { invisible: !isHovered })}
                                >
                                    <NSIcon icon={IconGripVertical} className="text-muted" />
                                </span>
                            </RenderIf>
                            <div className="d-flex align-items-center">
                                <RenderIf isTrue={Boolean(subcategory.children?.length)}>
                                    <NSIcon
                                        className="icon ml-1 mr-2 flex-shrink-0 ReadOnlyWrapper--enable-pointer-events"
                                        role="presentation"
                                        onClick={() => updateExpandedCategories(null, subcategory.id)}
                                        icon={expandedCategories.subcategories.includes(subcategory.id) ? IconCaretDownFilled : IconCaretRightFilled}
                                    />
                                </RenderIf>
                                <NSCellInput
                                    value={subcategory.description}
                                    handleUpdateValue={updatedValue => {
                                        handleUpdateBudgetClassifications([{ id: subcategory.id, description: updatedValue as string }]);
                                    }}
                                    placeholder="Enter subcategory name"
                                    customClassName={subcategory.children?.length ? '' : 'ml-4'}
                                    disabled={isPreview}
                                />
                            </div>
                        </div>
                        <div className="d-flex flex-row flex-nowrap align-items-center">
                            <RenderIf isTrue={hasNoSelectedCategories}>
                                <NSButton
                                    icon={IconTrash}
                                    size="sm"
                                    className={classNames('px-2 mr-1 text-muted hover:text-dark bg-none hover:bg-light', { invisible: !isHovered })}
                                    callback={() => isHovered && toggleDeleteSubcategoryModal()}
                                    isLoading={isBudgetClassificationDeletionLoading}
                                />
                            </RenderIf>

                            <NSButton
                                icon={IconPlus}
                                size="sm"
                                className={classNames('px-2 mr-2 text-muted hover:text-dark bg-none hover:bg-light', { invisible: !isHovered })}
                                callback={createBudgetLine}
                                isLoading={isBudgetClassificationLoading}
                            />
                        </div>
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td">
                    <div className="d-flex justify-content-end text-dark">
                        {selectedUnitTotal != null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border pr-2">
                    <div className="d-flex justify-content-end text-dark">
                        {subcategory.amount != null ? numeralFormatterCurrency(subcategory.amount, showCents ? '$0,0.00' : '$0,0') : '—'}
                    </div>
                </td>
                {projectionPeriods?.map((period, index) => {
                    const targetPeriod = subcategoryPeriods[index];

                    return (
                        <PeriodCellValue
                            key={`uses-subcategory-period-${period.value}`}
                            tooltipId={`uses-subcategory-period-${index}`}
                            period={period}
                            value={targetPeriod?.projected === null || subcategory.amount === null
                                ? '—' : numeralFormatterCurrency(targetPeriod?.projected, showCents ? '$0,0.00' : '$0,0')}
                            cellClassName="NSTable__tbody__tr__td text-dark text-right"
                        />
                    );
                })}
            </tr>

            <RenderIf isTrue={expandedCategories.subcategories.includes(subcategory.id)}>
                <DndContext
                    sensors={linesSensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleBudgetLineDragEnd}
                >
                    <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
                        {orderedChildren.map(budgetClassification => (
                            <UsesTableBudgetLine
                                key={budgetClassification.id}
                                budgetClassification={budgetClassification}
                                unitType={unitType}
                                projectionPeriods={projectionPeriods}
                                isPreview={isPreview}
                                parentDragStyle={childDragStyle}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </RenderIf>

            <RenderIf isTrue={openDeleteSubcategoryModal}>
                <ConfirmationModal
                    toggle={toggleDeleteSubcategoryModal}
                    isOpen={openDeleteSubcategoryModal}
                    onConfirm={deleteSubcategory}
                    onCancel={toggleDeleteSubcategoryModal}
                    modalHeaderText={`Delete ${subcategory.description}`}
                    modalFooterButtonText="Yes, delete"
                    modalFooterButtonIsLoading={isBudgetClassificationDeletionLoading}
                    danger
                >
                    <div className="text-dark font-weight-bold mb-1">
                        Are you sure you want to delete
                        {subcategory.description}
                        ?
                    </div>
                    <div>
                        Your work will not be able to be recovered. Any child content within
                        {subcategory.description}
                        {' '}
                        will also be deleted.
                    </div>
                </ConfirmationModal>
            </RenderIf>
        </>
    );
};

export default UsesTableSubcategory;
